/**
 * QC (08 + 00c) data layer — the ONLY place this feature talks to the server. Every path + capability
 * is code-verified against server/src/modules/qc/{routes,controller,validators}.js. Server data lives
 * only in TanStack Query; mutations invalidate the affected lab-scoped keys. Request bodies are
 * snake_case (zod-validated server-side); the server derives laboratoireId + utilisateurId from req.user.
 *
 * Endpoints consumed (all under /api/v1, Biologiste authority — QC never leaves the node, L-4):
 *   GET   /qc/lots                       qc.lot.manage
 *   POST  /qc/lots                       qc.lot.manage   (enrol → brouillon)
 *   POST  /qc/lots/:id/valider           qc.lot.manage   (freeze cible/ecart_type → valide)
 *   POST  /qc/lots/sweep                 qc.lot.manage   (idempotent expiry sweep)
 *   GET   /qc/retenues                   qc.retenue.read (open holds worklist)
 *   GET   /qc/retenues/est-retenu        qc.retenue.read (pure containment probe)
 *   POST  /qc/retenues                   qc.retenue.open (manual hold)
 *   POST  /qc/retenues/:id/lever         qc.retenue.lift (lever)
 *   POST  /qc/observations               qc.controle.run (record → auto-evaluate Westgard)
 *   GET   /qc/points                     qc.point.read   (Levey-Jennings series + ±SD bands)
 */
import { api, qk, useApiMutation, useApiQuery } from "@/api";
import { useAuthStore } from "@/auth";
import type {
  ChartSeries,
  HoldLifted,
  HoldOpened,
  Id,
  LotControle,
  LotEnrolled,
  LotValidated,
  Niveau,
  ObservationResult,
  RetenueQc,
  SweepResult,
} from "./types";

/** Lab id for query scoping — from the live auth session (multi-tenant cache isolation). */
export function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? s.lab?.id ?? null);
}

/** Lab-scoped query-key sub-factory for the qc feature. */
export const qcKeys = {
  lots: (labId: string | null) => qk.scoped(labId, "qc", "lots"),
  retenues: (labId: string | null) => qk.scoped(labId, "qc", "retenues"),
  chart: (
    labId: string | null,
    parametreCode: string,
    extra: Record<string, string | undefined>,
  ) => qk.scoped(labId, "qc", "points", parametreCode, extra),
};

/** Drop undefined/empty keys so query-strings stay clean + cache keys stable. */
function prune<T extends Record<string, unknown>>(obj: T): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && v !== "") out[k] = String(v);
  }
  return out;
}

// ── Queries ──────────────────────────────────────────────────────────────────────

/** The QC lots for the tenant (most-recent-first server-side). */
export function useLots(labId: string | null, enabled: boolean) {
  return useApiQuery<LotControle[]>(qcKeys.lots(labId), "/qc/lots", {
    enabled,
    staleTime: 30_000,
  });
}

/** The open hold worklist. */
export function useHolds(labId: string | null, enabled: boolean) {
  return useApiQuery<RetenueQc[]>(qcKeys.retenues(labId), "/qc/retenues", {
    enabled,
    staleTime: 15_000,
  });
}

export interface ChartFilter {
  parametre_code: string;
  lot_controle_id?: string;
  niveau?: Niveau | "";
  automate_id?: string;
}

/** The Levey-Jennings series + ±1/2/3 SD bands for a (parametre[, lot, niveau, automate]) filter. */
export function useChart(labId: string | null, filter: ChartFilter, enabled: boolean) {
  const query = prune({
    parametre_code: filter.parametre_code,
    lot_controle_id: filter.lot_controle_id,
    niveau: filter.niveau,
    automate_id: filter.automate_id,
  });
  return useApiQuery<ChartSeries>(
    qcKeys.chart(labId, filter.parametre_code, {
      lot: filter.lot_controle_id,
      niveau: filter.niveau || undefined,
      automate: filter.automate_id,
    }),
    "/qc/points",
    { enabled: enabled && filter.parametre_code.trim() !== "", query, staleTime: 10_000 },
  );
}

// ── Lot mutations ──────────────────────────────────────────────────────────────────

export interface EnrolLotVars {
  code: string;
  fabricant?: string;
  niveau: Niveau;
  parametre_code: string;
  automate_id?: string;
  cible?: number;
  ecart_type?: number;
  unite?: string;
  date_peremption: string;
}

/** POST /qc/lots — enrol a lot (brouillon, editable). Refreshes the lots list. */
export function useEnrolLot(labId: string | null) {
  return useApiMutation<LotEnrolled, EnrolLotVars>(
    (vars) => {
      const body: Record<string, unknown> = {
        code: vars.code,
        niveau: vars.niveau,
        parametre_code: vars.parametre_code,
        date_peremption: vars.date_peremption,
      };
      if (vars.fabricant) body.fabricant = vars.fabricant;
      if (vars.automate_id) body.automate_id = vars.automate_id;
      if (vars.cible !== undefined) body.cible = vars.cible;
      if (vars.ecart_type !== undefined) body.ecart_type = vars.ecart_type;
      if (vars.unite) body.unite = vars.unite;
      return api.post<LotEnrolled>("/qc/lots", body);
    },
    { invalidate: [qcKeys.lots(labId)] },
  );
}

export interface ValiderLotVars {
  lotId: string;
  cible: number;
  ecart_type: number;
}

/** POST /qc/lots/:id/valider — FREEZE cible/ecart_type → valide (ecart_type must be > 0; 409 if not brouillon). */
export function useValiderLot(labId: string | null) {
  return useApiMutation<LotValidated, ValiderLotVars>(
    ({ lotId, cible, ecart_type }) =>
      api.post<LotValidated>(`/qc/lots/${lotId}/valider`, { cible, ecart_type }),
    { invalidate: [qcKeys.lots(labId)] },
  );
}

export interface SweepVars {
  as_of?: string;
}

/** POST /qc/lots/sweep — idempotent expiry sweep. Refreshes the lots list (statuts may flip). */
export function useSweepLots(labId: string | null) {
  return useApiMutation<SweepResult, SweepVars>(
    (vars) => api.post<SweepResult>("/qc/lots/sweep", vars.as_of ? { as_of: vars.as_of } : {}),
    { invalidate: [qcKeys.lots(labId)] },
  );
}

// ── Hold (retenue) mutations ─────────────────────────────────────────────────────────

export interface OpenHoldVars {
  parametre_code: string;
  automate_id?: string;
  fenetre_debut: string;
  fenetre_fin: string;
  motif: string;
}

/** POST /qc/retenues — open a manual biologiste hold (mandatory motif ≥ 2). Refreshes the worklist. */
export function useOpenHold(labId: string | null) {
  return useApiMutation<HoldOpened, OpenHoldVars>(
    (vars) => {
      const body: Record<string, unknown> = {
        parametre_code: vars.parametre_code,
        fenetre_debut: vars.fenetre_debut,
        fenetre_fin: vars.fenetre_fin,
        motif: vars.motif,
      };
      if (vars.automate_id) body.automate_id = vars.automate_id;
      return api.post<HoldOpened>("/qc/retenues", body);
    },
    { invalidate: [qcKeys.retenues(labId)] },
  );
}

export interface LiftHoldVars {
  retenueId: string;
}

/** POST /qc/retenues/:id/lever — lift a hold (ouverte → levee; 409 if already levee). Refreshes the worklist. */
export function useLiftHold(labId: string | null) {
  return useApiMutation<HoldLifted, LiftHoldVars>(
    ({ retenueId }) => api.post<HoldLifted>(`/qc/retenues/${retenueId}/lever`, {}),
    { invalidate: [qcKeys.retenues(labId)] },
  );
}

// ── Observation entry → auto-evaluate ─────────────────────────────────────────────────

export interface ObservationVars {
  automate_id?: string;
  parametre_code: string;
  niveau: Niveau;
  valeur: number;
  ts?: string;
}

/**
 * POST /qc/observations — record a QC observation; the server auto-evaluates (Westgard → forward-hold).
 * Returns the verdict + violations + z, OR a tagged { degraded } when there's no valide lot. Refreshes
 * the chart (a new point lands) + the holds worklist (a rejete may open one).
 */
export function useRecordObservation(labId: string | null) {
  return useApiMutation<ObservationResult, ObservationVars>(
    (vars) => {
      const body: Record<string, unknown> = {
        parametre_code: vars.parametre_code,
        niveau: vars.niveau,
        valeur: vars.valeur,
      };
      if (vars.automate_id) body.automate_id = vars.automate_id;
      if (vars.ts) body.ts = vars.ts;
      return api.post<ObservationResult>("/qc/observations", body);
    },
    {
      invalidate: [
        qcKeys.retenues(labId),
        // The chart for the observed parametre is scoped under qc/points/<code>; a broad qc/points
        // invalidation refreshes whichever series is open.
        qk.scoped(labId, "qc", "points"),
      ],
    },
  );
}

/** Re-export the imperative est-retenu probe (used by a quick containment check; not cached). */
export function probeRetenu(query: {
  parametre_code: string;
  automate_id?: string;
  instant?: string;
}): Promise<{ retenu: boolean }> {
  return api.get<{ retenu: boolean }>("/qc/retenues/est-retenu", prune(query));
}

export type { Id };
